import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { Button } from 'src/components/Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { InviteActiveWorkersDialogProps } from './types';
import { useCreateJobOfferBatch, useGetJobs } from 'src/api/hooks/useJob';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';

export const InviteActiveWorkersDialog: React.FC<InviteActiveWorkersDialogProps> = ({
  isOpen,
  handleClose,
  onSuccess,
  selectedWorkers,
}) => {
  const [selectedJobId, setSelectedJobId] = useState<string>('');

  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);

  const { t } = useTranslation();

  const [createJobOfferBatch, { loading: isOfferLoading }] = useCreateJobOfferBatch({
    onComplete: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.success,
        message: t('workersScreen.alerts.createOffer.success'),
      });
      handleClose();
      onSuccess();
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('workersScreen.alerts.createOffer.error'),
      });
    },
  });

  const [getJobs, { loading: isJobsLoading, data: jobs }] = useGetJobs();

  useEffect(() => {
    if (isOpen) {
      setSelectedJobId('');
      getJobs();
    }
  }, [isOpen]);

  const isLoading = isJobsLoading || isOfferLoading;

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>
        <Trans>workersScreen.chooseJob</Trans>
      </DialogTitle>
      <DialogContent>
        <DialogContentText mb={2}>
          <Trans>workersScreen.confirmJob</Trans>
        </DialogContentText>
        <Autocomplete
          onChange={(_, job) => setSelectedJobId(job?.id || '')}
          options={jobs || []}
          disabled={isLoading}
          getOptionDisabled={job => job.id === selectedJobId}
          getOptionLabel={option => option.role.name || ''}
          value={jobs?.find(job => job.id === selectedJobId) || null}
          renderInput={params => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isJobsLoading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <DialogActions sx={{ justifyContent: 'space-between', px: 0 }}>
          <Button
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.DARK}
            onClick={handleClose}
            disabled={isLoading}>
            <Trans>generic.cancel</Trans>
          </Button>
          <Button
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.DARK}
            disabled={!selectedJobId || isLoading}
            onClick={() => {
              createJobOfferBatch({
                variables: {
                  offers: selectedWorkers.map(worker => ({
                    workerId: worker.id,
                    jobId: selectedJobId,
                  })),
                },
              });
            }}>
            <Trans>workersScreen.inviteWorkers</Trans>
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
